import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

/**
 * Site-wide visibility state.
 * Statuses per page key: "live" | "coming_soon" | "hidden"
 * Loaded once on app mount from GET /api/page-visibility.
 */
const VisibilityContext = createContext({ loading: true, byKey: {}, pages: [], reload: () => {} });

export function PageVisibilityProvider({ children }) {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/page-visibility");
      setPages(data?.pages || []);
    } catch {
      setPages([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Refresh visibility when the tab regains focus — so the admin sees their
    // own status changes reflected without a hard reload after saving.
    const onVis = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onVis);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onVis);
    };
  }, []);

  const byKey = useMemo(() => {
    const map = {};
    for (const p of pages) map[p.key] = p;
    return map;
  }, [pages]);

  return (
    <VisibilityContext.Provider value={{ loading, byKey, pages, reload: load }}>
      {children}
    </VisibilityContext.Provider>
  );
}

export function usePageVisibility() {
  return useContext(VisibilityContext);
}

/** Convenience — true only when admin has explicitly set status="live" OR the page is unmanaged (default visible). */
export function useIsPageVisible(pageKey) {
  const { byKey, loading } = usePageVisibility();
  if (loading) return true; // optimistic — render until we know
  const entry = byKey[pageKey];
  if (!entry) return true;  // unmanaged page → always visible
  return entry.status !== "hidden";
}
